import AppIcon from "common/app-icon";
import type { AppKind } from "common/components/app-kind";
import { getAnyLocalizedString } from "common/components/localization";
import type { DevStringKey } from "common/components/localization/dev";
import type { LocalizedStringKey } from "common/components/localization/types";
import { ActionResult } from "common/components/types";
import { Appearance } from "generator/components/button-appearance";
import { safeURL } from "common/Support";
import type { OnClickHandler } from "hooks/use-on-click-from-action";
import * as React from "react";
import { useTheme } from "styled-components";
import type { Subtract } from "utility-types";
import { withScrollOverlayID } from "webapp/chrome/views/scroll-view/scroll-view-overlay";
import type { RuntimeTheme } from "webapp/lib/theme/core-themes/runtime-theme";
import { FloatingButtonSimple } from "webapp/ui/views/floating-button/floating-button";
import { PortalObserver } from "webapp/ui/views/portal-observer/portal-observer";
import { trackLinkClick } from "../../../../analytics/link-clicks";
import { getUrlHandling, UrlHandling } from "../../../lib/link-utils";
import type { ButtonViewProps } from "../../views/button/button-view";
import { ButtonView } from "../../views/button/button-view";

function getURLHandling(url: string | undefined): {
    url: string | undefined;
    useTag: boolean;
    target: "_blank" | undefined;
} {
    url = safeURL(url);
    if (url === undefined) {
        return { url: url, useTag: false, target: undefined };
    }
    const handling = getUrlHandling(url, false);
    const useTag = handling === UrlHandling.UseATag || handling === UrlHandling.UseATagWithBlank;
    const target = handling === UrlHandling.UseATagWithBlank ? "_blank" : undefined;
    return { url: url, useTag, target };
}

interface Handled {
    readonly useTag?: boolean;
    readonly showConfirm?: boolean;
    readonly onShowConfirm?: (show: boolean) => void;
    readonly onClick?: (handled: boolean) => void;
}

interface Props extends Subtract<ButtonViewProps, Handled> {
    readonly scrollOverlayID: string;
    readonly floatingIcon?: string;
    readonly appKind: AppKind;
    readonly onClick: OnClickHandler;
}

const Button: React.FunctionComponent<Props> = p => {
    const [showConfirm, setShowConfirm] = React.useState(false);
    const [confirmationLocalizedString, setConfirmationLocalizedString] = React.useState<
        LocalizedStringKey | DevStringKey | undefined
    >(undefined);
    const [disabled, setDisabled] = React.useState(false);

    const { onClick, floatingIcon } = p;
    const { url, useTag } = getURLHandling(p.url);

    let title = p.title;
    if (confirmationLocalizedString !== undefined) {
        title = getAnyLocalizedString(confirmationLocalizedString, p.appKind);
    }

    const onButtonClick = React.useCallback(
        async (handled: boolean) => {
            if (onClick === undefined) return;
            if (confirmationLocalizedString !== undefined) return;

            if (url !== undefined) {
                trackLinkClick(url);
            }

            const result = await onClick(handled);

            if (result === ActionResult.CopiedToClipboard) {
                setConfirmationLocalizedString("copied");
                setTimeout(() => {
                    setConfirmationLocalizedString(undefined);
                }, 2000);
            }
        },
        [onClick, confirmationLocalizedString, url]
    );

    const onShowConfirm = React.useCallback((show: boolean) => {
        setShowConfirm(show);
        setDisabled(show);
    }, []);

    const theme: RuntimeTheme = useTheme() as RuntimeTheme;

    if (p.appearance === Appearance.Floating) {
        return (
            <PortalObserver selector={`#${p.scrollOverlayID}fab-target`}>
                <FloatingButtonSimple
                    {...p}
                    isEnabled={!p.disabled && !disabled}
                    onClick={() => onButtonClick(url === undefined)}
                    title={title}>
                    <AppIcon icon={floatingIcon ?? ""} size={22} color={theme.accentForegroundColor} />
                </FloatingButtonSimple>
            </PortalObserver>
        );
    }

    return (
        <ButtonView
            {...p}
            onClick={onButtonClick}
            title={title}
            disabled={p.disabled || disabled}
            url={url}
            useTag={useTag}
            showConfirm={showConfirm}
            icon={floatingIcon}
            onShowConfirm={onShowConfirm}
        />
    );
};

export default withScrollOverlayID(Button);
